import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, Upload, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { VenueImageManager } from "@/components/admin/VenueImageManager";
import { getUserFriendlyError, validateVenueRow, sanitizeCsvCell } from "@/lib/error-handler";

type Venue = Tables<"venues">;

const VENUE_TYPES = ["sala", "teatro", "auditorio", "exterior"] as const;
const VENUE_STATUSES = ["active", "draft", "archived"] as const;

const emptyVenue: Partial<TablesInsert<"venues">> = {
  name: "",
  city: "",
  autonomous_community: "",
  address_street: "",
  address_number: "",
  postal_code: "",
  capacity: undefined,
  official_price: undefined,
  official_price_notes: "",
  email: "",
  phone: "",
  website: "",
  instagram: "",
  type: "sala",
  status: "active",
  description: "",
};

export default function AdminVenues() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [form, setForm] = useState<Partial<TablesInsert<"venues">>>(emptyVenue);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navTo = useNavigate();

  const { data: venues, isLoading } = useQuery({
    queryKey: ["admin-venues"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("venues")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Venue[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (venueData: Partial<TablesInsert<"venues">>) => {
      if (editingVenue) {
        const { data, error } = await supabase
          .from("venues")
          .update(venueData as TablesUpdate<"venues">)
          .eq("id", editingVenue.id)
          .select();
        if (error) throw error;
        if (!data || data.length === 0) throw new Error("No se pudo actualizar la sala. Verifica tus permisos.");
      } else {
        const { error } = await supabase
          .from("venues")
          .insert(venueData as TablesInsert<"venues">);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-venues"] });
      queryClient.invalidateQueries({ queryKey: ["venues"] });
      toast({ title: editingVenue ? "Sala actualizada" : "Sala creada" });
      closeDialog();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("venues").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-venues"] });
      toast({ title: "Sala eliminada" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  const csvMutation = useMutation({
    mutationFn: async (rows: Partial<TablesInsert<"venues">>[]) => {
      const { error } = await supabase.from("venues").insert(rows as TablesInsert<"venues">[]);
      if (error) throw error;
      return rows.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["admin-venues"] });
      toast({ title: `${count} salas importadas correctamente` });
      setCsvDialogOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error al importar", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  function closeDialog() {
    setDialogOpen(false);
    setEditingVenue(null);
    setForm(emptyVenue);
  }

  function openEdit(venue: Venue) {
    setEditingVenue(venue);
    setForm({
      name: venue.name,
      city: venue.city,
      autonomous_community: venue.autonomous_community ?? "",
      address_street: venue.address_street ?? "",
      address_number: venue.address_number ?? "",
      postal_code: venue.postal_code ?? "",
      capacity: venue.capacity ?? undefined,
      official_price: venue.official_price ?? undefined,
      official_price_notes: venue.official_price_notes ?? "",
      email: venue.email ?? "",
      phone: venue.phone ?? "",
      website: venue.website ?? "",
      instagram: venue.instagram ?? "",
      type: venue.type ?? "sala",
      status: venue.status ?? "active",
      description: venue.description ?? "",
    });
    setDialogOpen(true);
  }

  function handleSave() {
    if (!form.name || !form.city) {
      toast({ title: "Nombre y ciudad son obligatorios", variant: "destructive" });
      return;
    }
    // Clean empty strings to null for optional fields
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(form)) {
      if (value === "" || value === undefined) {
        cleaned[key] = null;
      } else {
        cleaned[key] = value;
      }
    }
    // Ensure required fields are not null
    cleaned.name = form.name;
    cleaned.city = form.city;
    saveMutation.mutate(cleaned as Partial<TablesInsert<"venues">>);
  }

  function handleCSV(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split("\n").filter((l) => l.trim());
      if (lines.length < 2) {
        toast({ title: "CSV vacío o sin datos", variant: "destructive" });
        return;
      }

      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const rows: Partial<TablesInsert<"venues">>[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        const row: Record<string, any> = {};
        headers.forEach((h, idx) => {
          const val = sanitizeCsvCell(values[idx]?.trim() ?? "");
          if (!val) return;
          const mapped = mapCSVHeader(h);
          if (mapped) {
            if (mapped === "capacity") row[mapped] = parseInt(val) || null;
            else if (mapped === "official_price") row[mapped] = parseFloat(val) || null;
            else if (mapped === "latitude" || mapped === "longitude") row[mapped] = parseFloat(val) || null;
            else row[mapped] = val;
          }
        });
        if (row.name && row.city) rows.push(row);
      }

      if (rows.length === 0) {
        toast({ title: "No se encontraron filas válidas", variant: "destructive" });
        return;
      }

      // Validate all rows before insert
      const invalidRows = rows
        .map((r, i) => ({ row: i + 2, error: validateVenueRow(r as Record<string, any>) }))
        .filter((x) => x.error);
      if (invalidRows.length > 0) {
        const details = invalidRows.slice(0, 5).map((x) => `Fila ${x.row}: ${x.error}`).join("\n");
        toast({
          title: `${invalidRows.length} fila(s) con errores`,
          description: details,
          variant: "destructive",
        });
        return;
      }

      csvMutation.mutate(rows);
    };
    reader.readAsText(file);
  }

  const filteredVenues = venues?.filter((v) =>
    !searchQuery ||
    v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.city.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statusColors: Record<string, string> = {
    active: "bg-success/10 text-success",
    draft: "bg-warning/10 text-warning",
    archived: "bg-muted text-muted-foreground",
  };

  return (
    <AdminLayout requiredModule="salas" requiredPermission="can_view">
      <div>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">Gestión de Salas</h1>
            <p className="text-sm text-muted-foreground">{venues?.length ?? 0} salas en total</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Upload className="mr-2 h-4 w-4" />
                  Importar CSV
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Importar Salas desde CSV</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    El CSV debe tener cabeceras. Columnas soportadas: nombre, ciudad, comunidad, dirección, 
                    código postal, aforo, precio, email, teléfono, web, instagram, tipo, descripción.
                  </p>
                  <Input type="file" accept=".csv" onChange={handleCSV} disabled={csvMutation.isPending} />
                  {csvMutation.isPending && <p className="text-sm text-muted-foreground">Importando…</p>}
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); else { setForm(emptyVenue); setDialogOpen(true); } }}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Nueva Sala
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingVenue ? "Editar Sala" : "Nueva Sala"}</DialogTitle>
                </DialogHeader>
                <VenueForm form={form} setForm={setForm} onSave={handleSave} saving={saveMutation.isPending} />
                {editingVenue && (
                  <div className="mt-4 border-t pt-4">
                    <VenueImageManager venueId={editingVenue.id} />
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="mb-4">
          <Input
            placeholder="Buscar por nombre o ciudad…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm"
          />
        </div>

        {isLoading ? (
          <Skeleton className="h-64" />
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Ciudad</TableHead>
                    <TableHead className="hidden md:table-cell">Tipo</TableHead>
                    <TableHead className="hidden md:table-cell">Aforo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVenues?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                        <Building2 className="mx-auto mb-2 h-8 w-8 opacity-40" />
                        No hay salas
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredVenues?.map((venue) => (
                      <TableRow key={venue.id}>
                        <TableCell className="font-medium">{venue.name}</TableCell>
                        <TableCell>{venue.city}</TableCell>
                        <TableCell className="hidden capitalize md:table-cell">{venue.type}</TableCell>
                        <TableCell className="hidden md:table-cell">{venue.capacity ?? "—"}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[venue.status ?? "active"]}>{venue.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => navTo(`/admin/venues/${venue.id}/edit`)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                if (confirm("¿Eliminar esta sala?")) deleteMutation.mutate(venue.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}

// -- Sub-components --

function VenueForm({
  form,
  setForm,
  onSave,
  saving,
}: {
  form: Partial<TablesInsert<"venues">>;
  setForm: (f: Partial<TablesInsert<"venues">>) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const set = (key: string, value: any) => setForm({ ...form, [key]: value });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Label>Nombre *</Label>
        <Input value={form.name ?? ""} onChange={(e) => set("name", e.target.value)} />
      </div>
      <div>
        <Label>Ciudad *</Label>
        <Input value={form.city ?? ""} onChange={(e) => set("city", e.target.value)} />
      </div>
      <div>
        <Label>Comunidad Autónoma</Label>
        <Input value={form.autonomous_community ?? ""} onChange={(e) => set("autonomous_community", e.target.value)} />
      </div>
      <div>
        <Label>Calle</Label>
        <Input value={form.address_street ?? ""} onChange={(e) => set("address_street", e.target.value)} />
      </div>
      <div>
        <Label>Número</Label>
        <Input value={form.address_number ?? ""} onChange={(e) => set("address_number", e.target.value)} />
      </div>
      <div>
        <Label>Código Postal</Label>
        <Input value={form.postal_code ?? ""} onChange={(e) => set("postal_code", e.target.value)} />
      </div>
      <div>
        <Label>Aforo</Label>
        <Input type="number" value={form.capacity ?? ""} onChange={(e) => set("capacity", e.target.value ? parseInt(e.target.value) : null)} />
      </div>
      <div>
        <Label>Precio Oficial (€)</Label>
        <Input type="number" value={form.official_price ?? ""} onChange={(e) => set("official_price", e.target.value ? parseFloat(e.target.value) : null)} />
      </div>
      <div>
        <Label>Notas Precio</Label>
        <Input value={form.official_price_notes ?? ""} onChange={(e) => set("official_price_notes", e.target.value)} />
      </div>
      <div>
        <Label>Email</Label>
        <Input type="email" value={form.email ?? ""} onChange={(e) => set("email", e.target.value)} />
      </div>
      <div>
        <Label>Teléfono</Label>
        <Input value={form.phone ?? ""} onChange={(e) => set("phone", e.target.value)} />
      </div>
      <div>
        <Label>Web</Label>
        <Input value={form.website ?? ""} onChange={(e) => set("website", e.target.value)} />
      </div>
      <div>
        <Label>Instagram</Label>
        <Input value={form.instagram ?? ""} onChange={(e) => set("instagram", e.target.value)} />
      </div>
      <div>
        <Label>Tipo</Label>
        <Select value={form.type ?? "sala"} onValueChange={(v) => set("type", v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {VENUE_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Estado</Label>
        <Select value={form.status ?? "active"} onValueChange={(v) => set("status", v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {VENUE_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="sm:col-span-2">
        <Label>Descripción</Label>
        <Textarea rows={3} value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} />
      </div>
      <div className="sm:col-span-2 flex justify-end">
        <Button onClick={onSave} disabled={saving}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
}

// -- CSV helpers --

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (const char of line) {
    if (char === '"') { inQuotes = !inQuotes; }
    else if (char === "," && !inQuotes) { result.push(current); current = ""; }
    else { current += char; }
  }
  result.push(current);
  return result;
}

function mapCSVHeader(header: string): string | null {
  const map: Record<string, string> = {
    nombre: "name", name: "name",
    ciudad: "city", city: "city",
    comunidad: "autonomous_community", autonomous_community: "autonomous_community",
    "comunidad autónoma": "autonomous_community", "comunidad autonoma": "autonomous_community",
    calle: "address_street", address_street: "address_street", dirección: "address_street", direccion: "address_street",
    número: "address_number", numero: "address_number", address_number: "address_number",
    "código postal": "postal_code", "codigo postal": "postal_code", cp: "postal_code", postal_code: "postal_code",
    aforo: "capacity", capacity: "capacity",
    precio: "official_price", official_price: "official_price", "precio oficial": "official_price",
    email: "email",
    teléfono: "phone", telefono: "phone", phone: "phone",
    web: "website", website: "website",
    instagram: "instagram",
    tipo: "type", type: "type",
    descripción: "description", descripcion: "description", description: "description",
    latitud: "latitude", latitude: "latitude",
    longitud: "longitude", longitude: "longitude",
  };
  return map[header] ?? null;
}
